import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { setMessage } from '../message';
import RoleService from '../../services/roleServices';
import { clearAuth } from 'store/auth';

export const getList = createAsyncThunk('role/list', async (_, thunkAPI) => {
  try {
    const response = await RoleService.getList();
    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const createData = createAsyncThunk('role/create', async (data, thunkAPI) => {
  try {
    const response = await RoleService.create(data);
    thunkAPI.dispatch(getList());

    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    if (error.response.status == 404) {
      thunkAPI.dispatch(getList());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const readData = createAsyncThunk('role/read', async (id, thunkAPI) => {
  try {
    const response = await RoleService.read(id);
    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const updateData = createAsyncThunk('role/update', async (data, thunkAPI) => {
  try {
    const response = await RoleService.update(data.id, data);
    thunkAPI.dispatch(getList());

    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const deleteData = createAsyncThunk('role/delete', async (id, thunkAPI) => {
  try {
    const response = await RoleService.del(id);
    thunkAPI.dispatch(getList());

    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    if (error.response.status == 404) {
      thunkAPI.dispatch(getList());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const getPermissions = createAsyncThunk('role/getPermissions', async (_, thunkAPI) => {
  try {
    const response = await RoleService.getPermissions();
    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

export const updateRolePermission = createAsyncThunk('role/updateRolePermission', async (data, thunkAPI) => {
  try {
    const response = await RoleService.updateRolePermission(data.id, data);
    const message = (response && response.data && response.data.message) || response.message || response.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'success' }));

    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const changeStatus = createAsyncThunk('role/changeStatus', async (data, thunkAPI) => {
  try {
    const response = await RoleService.changeStatus(data.id, data);
    thunkAPI.dispatch(getList());
    return response.data;
  } catch (error) {
    if (error.response.status == 401) {
      thunkAPI.dispatch(clearAuth());
    }
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    thunkAPI.dispatch(setMessage({ message: message, type: 'error' }));
    return thunkAPI.rejectWithValue();
  }
});

const initialState = { isLoading: false, list: [], permission: [], data: [], error: [] };

const roleSlice = createSlice({
  name: 'role',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(getList.pending, (state) => {
      state.isLoading = true;
    }),
      builder.addCase(getList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload;
      }),
      builder.addCase(getList.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(createData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(createData.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(createData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.errors;
      }),
      builder.addCase(readData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(readData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      }),
      builder.addCase(readData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(updateData.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.errors;
      }),
      builder.addCase(deleteData.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(deleteData.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(deleteData.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateRolePermission.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(updateRolePermission.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(updateRolePermission.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(getPermissions.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(getPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permission = action.payload;
      }),
      builder.addCase(getPermissions.rejected, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(changeStatus.pending, (state) => {
        state.isLoading = true;
      }),
      builder.addCase(changeStatus.fulfilled, (state) => {
        state.isLoading = false;
      }),
      builder.addCase(changeStatus.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

const { reducer } = roleSlice;
export default reducer;
